import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, X, Pause, Play, Trash2, Mail, Check, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)tcl_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const STATUS_STYLES = {
  invited: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-red-100 text-red-600',
};

const AdminSpeakers = () => {
  const [speakers, setSpeakers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [actionLoading, setActionLoading] = useState({});
  const { token } = useAuth();

  const authHeaders = useCallback(() => {
    const csrf = getCsrfToken();
    const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (csrf) h['X-CSRF-Token'] = csrf;
    return h;
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [speakerRes, sessionRes, reqRes] = await Promise.all([
        fetch(`${API_URL}/api/sessions/speakers/list`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/sessions/list`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/sessions/admin/access-requests`, { headers: authHeaders(), credentials: 'include' }),
      ]);
      if (speakerRes.ok) setSpeakers((await speakerRes.json()).speakers || []);
      if (sessionRes.ok) setSessions((await sessionRes.json()).sessions || []);
      if (reqRes.ok) setAccessRequests((await reqRes.json()).requests || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/sessions/speakers/invite`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(`Invitation sent to ${form.email}`);
        setShowInvite(false);
        setForm({ name: '', email: '' });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Invitation failed');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const toggleSpeaker = async (id, action) => {
    await fetch(`${API_URL}/api/sessions/speakers/${id}/${action}`, { method: 'PATCH', headers: authHeaders(), credentials: 'include' });
    fetchData();
  };

  const removeSpeaker = async (id) => {
    if (!window.confirm('Remove this speaker?')) return;
    await fetch(`${API_URL}/api/sessions/speakers/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    fetchData();
  };

  const resendInvite = async (id, name) => {
    setActionLoading(p => ({ ...p, [`resend_${id}`]: true }));
    try {
      const res = await fetch(`${API_URL}/api/sessions/speakers/${id}/resend-invite`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) {
        toast.success(`Invite resent to ${name}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Resend failed');
      }
    } catch { toast.error('Network error'); }
    setActionLoading(p => ({ ...p, [`resend_${id}`]: null }));
  };

  const handleApproveRequest = async (reqId) => {
    setActionLoading(p => ({ ...p, [reqId]: 'approve' }));
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/access-requests/${reqId}/approve`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) {
        toast.success('Access approved — invite email sent');
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Approval failed');
      }
    } catch { toast.error('Network error'); }
    setActionLoading(p => ({ ...p, [reqId]: null }));
  };

  const handleRejectRequest = async (reqId) => {
    setActionLoading(p => ({ ...p, [reqId]: 'reject' }));
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/access-requests/${reqId}/reject`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) { toast.success('Request rejected'); fetchData(); }
    } catch {}
    setActionLoading(p => ({ ...p, [reqId]: null }));
  };

  if (loading) return <div className="py-16 text-center text-cream/20">Loading...</div>;

  return (
    <div data-testid="admin-speakers">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Speakers</h1>
          <p className="text-cream/30 text-sm mt-1">Invite speakers to facilitate CFQ sessions</p>
        </div>
        <button onClick={() => { setShowInvite(true); setForm({ name: '', email: '' }); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold text-navy text-sm font-medium rounded-lg hover:bg-gold/90" data-testid="invite-speaker-btn">
          <UserPlus size={16} /> Invite Speaker
        </button>
      </div>

      {/* ── Access Requests ── */}
      {accessRequests.filter(r => r.status === 'pending').length > 0 && (
        <div className="mb-6 border border-amber-500/20 rounded-xl overflow-hidden" data-testid="access-requests-section">
          <div className="bg-amber-500/10 px-5 py-3 flex items-center gap-2">
            <Clock size={14} className="text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">
              Facilitator Access Requests ({accessRequests.filter(r => r.status === 'pending').length})
            </span>
          </div>
          <div className="divide-y divide-amber-500/10">
            {accessRequests.filter(r => r.status === 'pending').map(req => (
              <div key={req.id} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={"access-req-" + req.id}>
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-medium">{req.name}</p>
                  <p className="text-cream/40 text-xs flex items-center gap-1 mt-0.5">
                    <Mail size={11} /> {req.email}
                    {req.organisation && <span className="ml-2 text-cream/25">· {req.organisation}</span>}
                  </p>
                  {req.message && (
                    <p className="text-cream/30 text-xs mt-1.5 italic leading-relaxed max-w-sm">"{req.message}"</p>
                  )}
                  <p className="text-cream/15 text-[10px] mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleApproveRequest(req.id)}
                    disabled={!!actionLoading[req.id]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-lg hover:bg-emerald-500/30 disabled:opacity-50"
                    data-testid={"approve-req-" + req.id}
                  >
                    <CheckCircle size={13} />
                    {actionLoading[req.id] === 'approve' ? 'Sending…' : 'Approve & Invite'}
                  </button>
                  <button
                    onClick={() => handleRejectRequest(req.id)}
                    disabled={!!actionLoading[req.id]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-400 text-xs rounded-lg hover:bg-red-500/20 disabled:opacity-50"
                    data-testid={"reject-req-" + req.id}
                  >
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-navy border border-gold/10 rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h2 className="text-cream text-lg font-semibold">Invite Facilitator</h2>
                <p className="text-cream/30 text-xs mt-0.5">A session code and access email will be sent automatically.</p>
              </div>
              <button onClick={() => { setShowInvite(false); setForm({ name: '', email: '' }); }} className="text-cream/30 hover:text-cream"><X size={18} /></button>
            </div>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="text-cream/50 text-xs block mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30" placeholder="Facilitator name" data-testid="speaker-name-input" />
              </div>
              <div>
                <label className="text-cream/50 text-xs block mb-1">Email</label>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} required type="email" className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30" placeholder="facilitator@example.com" data-testid="speaker-email-input" />
              </div>
              <button type="submit" className="w-full bg-gold text-navy py-2.5 rounded-lg text-sm font-medium hover:bg-gold/90" data-testid="speaker-invite-submit">Send Invitation</button>
            </form>
          </div>
        </div>
      )}

      {/* Speakers List */}
      {speakers.length === 0 ? (
        <div className="text-center py-20">
          <UserPlus size={32} className="text-cream/10 mx-auto mb-3" />
          <p className="text-cream/30 text-sm">No speakers yet. Invite one to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {speakers.map(s => (
            <div key={s.id} className="bg-navy border border-gold/[0.06] rounded-xl p-5 flex items-center justify-between" data-testid={`speaker-${s.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-cream font-medium">{s.name}</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || STATUS_STYLES.invited}`}>{s.status}</span>
                </div>
                <p className="text-cream/30 text-sm flex items-center gap-1"><Mail size={12} /> {s.email}</p>
                {s.assigned_sessions?.length > 0 && (
                  <p className="text-cream/20 text-xs mt-1">Sessions: {s.assigned_sessions.join(', ')}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                {s.status === 'invited' && (
                  <button
                    onClick={() => resendInvite(s.id, s.name)}
                    disabled={!!actionLoading[`resend_${s.id}`]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold/10 text-gold text-xs rounded-lg hover:bg-gold/20 disabled:opacity-50 transition-colors"
                    title="Resend invite email with session code"
                    data-testid={`resend-invite-${s.id}`}
                  >
                    <RefreshCw size={12} className={actionLoading[`resend_${s.id}`] ? 'animate-spin' : ''} />
                    {actionLoading[`resend_${s.id}`] ? 'Sending…' : 'Resend Invite'}
                  </button>
                )}
                {s.status === 'active' && (
                  <button onClick={() => toggleSpeaker(s.id, 'pause')} className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg" title="Pause access"><Pause size={16} /></button>
                )}
                {s.status === 'paused' && (
                  <button onClick={() => toggleSpeaker(s.id, 'activate')} className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg" title="Reactivate"><Play size={16} /></button>
                )}
                <button onClick={() => removeSpeaker(s.id)} className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg" title="Remove"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSpeakers;
